/**
 * CompareStateChip — the "Compared · 3d" / "Comparing…" pill Combine & Filter
 * round 2 (sub-task R2-B) renders next to a people list's title, via
 * `ImportPicker`'s `renderRowAdornment` slot.
 *
 * Reads `ImportSummary.compare` — the list's LAST Compare run (see
 * `imports.client.ts`'s `ImportCompareState`, a local TODO(r2 merge)
 * extension). Renders:
 *   - `"succeeded"` → "Compared · <relative>", with a `Tooltip` spelling out
 *     the exact date + the search it ran against (or "search unknown" for a
 *     pre-R2-A run that never recorded one — see `ImportCompareState`'s own
 *     docstring).
 *   - a `"succeeded"` run on a SELECTION of the list (`scope: "selection"`)
 *     → "Compared (partial) · <relative>": the rest of the list was not
 *     looked up, so the chip must not claim the list is compared (Combine
 *     does not count it either — /pr-review iter 2, Agent A §3 / Agent C N1).
 *     Its tooltip says Combine uses only a whole-list run and that the
 *     latest run replaces any earlier one — a selection run after a full
 *     one leaves the list unchecked (/pr-review iter 3, Agent C m3).
 *   - a `"succeeded"` run with no recorded scope (from before this version)
 *     → "Compared before update": Combine does not count it as checked, so
 *     the chip does not say plain "Compared" (/pr-review iter 3, Agent A
 *     §2). It states the list's status, like every other chip here; it is
 *     not an action ("Compare again" read like a button and was not one,
 *     iter 4 nit). The tooltip gives the date and says the list needs a
 *     new Compare (Combine & Filter starts one by itself for a selected
 *     list since task 2026-09-24-combine-more-filters, so it names no
 *     click to make).
 *   - a `"succeeded"` result a combined list INHERITED from its sources
 *     (`inherited_generated_at` set) → "Inherited · <age of the list>"
 *     ("Inherited (partial)" when not every row inherited), the tooltip giving
 *     the range of the inherited verdicts — see `InheritedChip`.
 *   - `"queued"` / `"running"` → "Comparing…", no tooltip (nothing to date
 *     yet) — unless `stale` (abandoned: its worker died), which renders
 *     nothing, like a failed run. Also "Comparing…" whenever `comparing` is
 *     set: a run the Combine page started or follows is in flight.
 *   - anything else (`"failed"`, or no `compare` at all) → nothing. A failed
 *     run gets no chip of its own — the plan's spec names exactly three
 *     states, and "never compared" is the honest reading of "not currently
 *     succeeded or in flight" for a list-row glance.
 */
import { Tooltip } from "@nu-assist/ui";

import type { ImportCompareState } from "../../../clients/imports.client";
import { daysAgo } from "../../person-panel/format";

/** `0` → "today", `1` → "1d", `5` → "5d". Compact on purpose — this sits in
 *  a dense picker row, unlike `fmtDaysAgo`'s prose form ("3 days ago") used
 *  elsewhere. */
function compactDaysAgo(iso: string, today: Date = new Date()): string {
  const days = daysAgo(iso, today);
  if (days == null) return "";
  return days === 0 ? "today" : `${days}d`;
}

function formatExactDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ComparingChip() {
  return (
    <span
      data-testid="compare-state-chip-comparing"
      className="rounded-pill border border-border bg-surface px-2 py-0.5 text-caption text-text-secondary"
    >
      Comparing…
    </span>
  );
}

/**
 * A combined list's result INHERITED from its sources' Compares
 * (combine-followups 04). Its `computed_at` is the OLDEST inherited verdict
 * (so a later Combine counts every inherited row as classified), which made a
 * list generated a minute ago read "Compared · 3d". The chip dates the list by
 * its generation; the tooltip gives the verdicts' range, `computed_at` (=
 * `compared_from`) to `inherited_compared_to`.
 */
function InheritedChip({
  compare,
  generatedAt,
  partial,
  chipClass,
}: {
  compare: ImportCompareState;
  generatedAt: string;
  partial: boolean;
  chipClass: string;
}) {
  const from = compare.computed_at ?? generatedAt;
  const to = compare.inherited_compared_to ?? from;
  const against = compare.search_id_thrive ? ` against search ${compare.search_id_thrive}` : "";
  const coverage = partial
    ? " Some people inherited no status, so Combine counts the list as checked only after a whole-list Compare."
    : "";
  const tooltipContent =
    `Inherited from Compares run between ${formatExactDate(from)} and ${formatExactDate(to)}` +
    `${against}. List generated ${formatExactDate(generatedAt)}.${coverage}`;
  return (
    <Tooltip content={tooltipContent}>
      <span data-testid="compare-state-chip-inherited" className={chipClass}>
        {partial ? "Inherited (partial)" : "Inherited"} · {compactDaysAgo(generatedAt)}
      </span>
    </Tooltip>
  );
}

export function CompareStateChip({
  compare,
  comparing = false,
}: {
  compare: ImportCompareState | null | undefined;
  /** A Compare the page itself started or follows is queued/running on this
   *  list. `compare` is the imports list's copy, re-read only when the run
   *  ends, so without this the chip kept the list's LAST run ("Compared
   *  before update") while the page's own card said "Comparing… 40%"
   *  (PR #962 follow-up F8). */
  comparing?: boolean;
}) {
  if (comparing) return <ComparingChip />;
  if (!compare) return null;

  if ((compare.status === "queued" || compare.status === "running") && !compare.stale) {
    return <ComparingChip />;
  }

  if (compare.status !== "succeeded" || !compare.computed_at) return null;

  const partial = compare.scope === "selection";
  const legacy = compare.scope == null;
  const chipClass =
    "rounded-pill border border-border bg-surface px-2 py-0.5 text-caption text-text-secondary";
  if (compare.inherited_generated_at) {
    return (
      <InheritedChip
        compare={compare}
        generatedAt={compare.inherited_generated_at}
        partial={partial}
        chipClass={chipClass}
      />
    );
  }

  const chip = legacy ? (
    <span data-testid="compare-state-chip-legacy" className={chipClass}>
      Compared before update
    </span>
  ) : (
    <span
      data-testid={partial ? "compare-state-chip-partial" : "compare-state-chip-compared"}
      className={chipClass}
    >
      {partial ? "Compared (partial)" : "Compared"} · {compactDaysAgo(compare.computed_at)}
    </span>
  );

  const when = `Compared ${formatExactDate(compare.computed_at)}`;
  const against = compare.search_id_thrive
    ? `against search ${compare.search_id_thrive}`
    : "· search unknown (run predates search tracking)";
  // What the list needs, not a click to make: Combine & Filter now starts
  // Compare by itself for a selected list (PR #962 follow-up F8).
  const coverage = partial
    ? " · selected people only, not the whole list. Combine uses only a whole-list Compare, and the latest run replaces any earlier one, so the list needs a whole-list Compare."
    : legacy
      ? " · Compared before this version, so Combine does not count it as checked; the list needs a new Compare."
      : "";
  const tooltipContent = `${when} ${against}${coverage}`;

  // `Tooltip`'s own trigger wrapper carries `tabIndex={0}` — this chip is
  // rendered as a SIBLING of the row's activation `<button>` (`ImportPicker`'s
  // own docblock on `renderRowAdornment`), never nested inside it, so this
  // adds one tab stop of its own rather than a second one for the button.
  return <Tooltip content={tooltipContent}>{chip}</Tooltip>;
}
